import { useMemo } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { CustomMonthPicker } from "./DatePickers";
import { isWeekOffDate, normalizeWeekOffDays } from "../../utils/weekOff";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function parseYm(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  return { year: y || new Date().getFullYear(), month: (m || 1) - 1 };
}

function dateToIso(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function LeaveMonthCalendar({
  yearMonth,
  onYearMonthChange,
  selected,
  onChange,
  minDate,
  maxDate,
  holidayDates = [],
  weekOffDays,
  extraWeekOffDates = [],
  halfDay = false,
}: {
  yearMonth: string;
  onYearMonthChange: (ym: string) => void;
  selected: string[];
  onChange: (dates: string[]) => void;
  minDate?: string;
  maxDate?: string;
  holidayDates?: string[];
  /** JS weekdays; Sunday always treated as off. Default Sunday only. */
  weekOffDays?: number[] | null;
  /** One-off week offs already marked on attendance for this employee. */
  extraWeekOffDates?: string[];
  halfDay?: boolean;
}) {
  const offs = useMemo(() => normalizeWeekOffDays(weekOffDays), [weekOffDays]);
  const holidaySet = useMemo(() => new Set(holidayDates), [holidayDates]);
  const extraOffSet = useMemo(() => new Set(extraWeekOffDates), [extraWeekOffDates]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const { year, month } = parseYm(yearMonth);

  const cells = useMemo(() => {
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const list: (string | null)[] = [];
    for (let i = 0; i < firstDow; i++) list.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      list.push(dateToIso(new Date(year, month, d)));
    }
    return list;
  }, [year, month]);

  const shiftMonth = (delta: number) => {
    const next = new Date(year, month + delta, 1);
    onYearMonthChange(
      `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`,
    );
  };

  const toggle = (iso: string) => {
    if (holidaySet.has(iso) || isWeekOffDate(iso, offs) || extraOffSet.has(iso)) return;
    if (minDate && iso < minDate) return;
    if (maxDate && iso > maxDate) return;
    if (halfDay) {
      onChange(selectedSet.has(iso) ? [] : [iso]);
      return;
    }
    if (selectedSet.has(iso)) {
      onChange(selected.filter((d) => d !== iso));
    } else {
      onChange([...selected, iso].sort());
    }
  };

  return (
    <div className="rounded-2xl border border-[#E8E8E4] bg-white overflow-hidden">
      <div className="flex items-center gap-2 border-b border-[#E8E8E4] bg-[#FDFBF2] p-3">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E8E8E4] bg-white text-[#4A4A48]"
          aria-label="Previous month"
        >
          <FaChevronLeft className="text-xs" />
        </button>
        <CustomMonthPicker
          label="Month"
          value={yearMonth}
          onChange={(ym) => {
            onYearMonthChange(ym);
          }}
          className="min-w-0 flex-1"
        />
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E8E8E4] bg-white text-[#4A4A48]"
          aria-label="Next month"
        >
          <FaChevronRight className="text-xs" />
        </button>
      </div>

      <div className="p-3">
        <div className="mb-2 grid grid-cols-7 gap-1">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="py-1 text-center text-[10px] font-semibold uppercase text-[#8A8A85]"
            >
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((iso, i) => {
            if (!iso) return <div key={`e-${i}`} />;
            const weekOff = isWeekOffDate(iso, offs) || extraOffSet.has(iso);
            const holiday = holidaySet.has(iso);
            const blocked =
              weekOff ||
              holiday ||
              (minDate ? iso < minDate : false) ||
              (maxDate ? iso > maxDate : false);
            const active = selectedSet.has(iso);
            return (
              <button
                key={iso}
                type="button"
                disabled={blocked}
                onClick={() => toggle(iso)}
                className={`h-9 rounded-lg text-sm font-medium transition ${
                  active
                    ? "bg-[#345952] text-white shadow-sm"
                    : blocked
                      ? "cursor-not-allowed text-[#D0D0CB]"
                      : "text-[#4A4A48] hover:bg-[#FDFBF2]"
                }`}
                title={
                  holiday
                    ? "Holiday"
                    : extraOffSet.has(iso)
                      ? "Custom week off"
                      : isWeekOffDate(iso, offs)
                        ? "Week off"
                        : blocked
                          ? "Not available"
                          : iso
                }
              >
                {Number(iso.slice(8))}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] leading-4 text-[#8A8A85]">
          {halfDay
            ? "Select one day for half day leave."
            : "Tap working days to select leave. Week offs and holidays are skipped."}
        </p>
        {selected.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {selected.map((iso) => (
              <span
                key={iso}
                className="rounded-full bg-[#E8F3EF] px-2 py-0.5 text-[11px] font-medium text-[#345952]"
              >
                {iso.slice(8)}/{iso.slice(5, 7)}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
